import React from 'react';
import { TensorShape } from '../../types/modelBuilder';

interface TensorVisualizerProps {
  shape: TensorShape;
  color?: string;
  size?: 'small' | 'medium' | 'large';
}

/**
 * Visual representation of tensor shapes
 * - 1D: line
 * - 2D: rectangle
 * - 3D: cube
 * - 4D+: grid of cubes
 */
export const TensorVisualizer: React.FC<TensorVisualizerProps> = ({
  shape,
  color = '#3B82F6',
  size = 'medium',
}) => {
  if (!shape || shape.length === 0) {
    return (
      <div style={{
        padding: '8px 12px',
        backgroundColor: '#F3F4F6',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#6B7280',
      }}>
        No shape
      </div>
    );
  }

  const sizeMap = {
    small: 40,
    medium: 60,
    large: 80,
  };

  const baseSize = sizeMap[size];

  // 1D - single bar
  if (shape.length === 1) {
    const width = Math.min(baseSize * 2, shape[0] * 2);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        <div
          style={{
            width: `${width}px`,
            height: `${baseSize / 3}px`,
            backgroundColor: color,
            borderRadius: '4px',
            opacity: 0.8,
          }}
        />
        <span style={{ fontSize: '11px', color: '#6B7280' }}>1D: [{shape[0]}]</span>
      </div>
    );
  }

  // 2D - rectangle
  if (shape.length === 2) {
    const [h, w] = shape;
    const ratio = w / h;
    const height = baseSize;
    const width = height * ratio;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        <div
          style={{
            width: `${Math.min(width, 120)}px`,
            height: `${baseSize}px`,
            backgroundColor: color,
            borderRadius: '4px',
            opacity: 0.8,
            border: `2px solid ${color}`,
          }}
        />
        <span style={{ fontSize: '11px', color: '#6B7280' }}>2D: [{shape[0]}×{shape[1]}]</span>
      </div>
    );
  }

  // 3D - single cube
  if (shape.length === 3) {
    const cubeSize = baseSize;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        <div
          style={{
            position: 'relative',
            width: `${cubeSize}px`,
            height: `${cubeSize}px`,
            transformStyle: 'preserve-3d',
            transform: 'rotateX(20deg) rotateY(30deg)',
          }}
        >
          {/* Front face */}
          <div
            style={{
              position: 'absolute',
              width: `${cubeSize}px`,
              height: `${cubeSize}px`,
              backgroundColor: color,
              opacity: 0.9,
              border: `2px solid ${color}`,
            }}
          />
          {/* Right face */}
          <div
            style={{
              position: 'absolute',
              width: `${cubeSize * 0.3}px`,
              height: `${cubeSize}px`,
              backgroundColor: color,
              opacity: 0.7,
              left: `${cubeSize}px`,
              border: `2px solid ${color}`,
            }}
          />
          {/* Top face */}
          <div
            style={{
              position: 'absolute',
              width: `${cubeSize}px`,
              height: `${cubeSize * 0.3}px`,
              backgroundColor: color,
              opacity: 0.6,
              border: `2px solid ${color}`,
            }}
          />
        </div>
        <span style={{ fontSize: '11px', color: '#6B7280' }}>
          3D: [{shape[0]}×{shape[1]}×{shape[2]}]
        </span>
      </div>
    );
  }

  // 4D+ - grid of cubes
  const numCubes = Math.min(4, shape[0]);
  const cubeSize = baseSize * 0.6;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(2, ${cubeSize}px)`, gap: '4px' }}>
        {Array.from({ length: numCubes }).map((_, i) => (
          <div
            key={i}
            style={{
              position: 'relative',
              width: `${cubeSize}px`,
              height: `${cubeSize}px`,
              transformStyle: 'preserve-3d',
              transform: 'rotateX(20deg) rotateY(30deg)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backgroundColor: color,
                opacity: 0.8,
                border: `1px solid ${color}`,
              }}
            />
          </div>
        ))}
        {shape[0] > 4 && (
          <div
            style={{
              width: `${cubeSize}px`,
              height: `${cubeSize}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: '#6B7280',
              fontWeight: 'bold',
            }}
          >
            +{shape[0] - 4}
          </div>
        )}
      </div>
      <span style={{ fontSize: '11px', color: '#6B7280' }}>
        {shape.length}D: [{shape.join('×')}]
      </span>
    </div>
  );
};

export default TensorVisualizer;
